import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { CheckCircle, AlertCircle, X, Clock } from 'lucide-react';

export interface ToastNotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
}

interface ToastProps {
  notification: ToastNotification;
  onClose: (id: string) => void;
}

const Toast: React.FC<ToastProps> = ({ notification, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    let dismissTimer: NodeJS.Timeout | undefined;
    if (!notification.persistent) {
      dismissTimer = setTimeout(() => handleClose(), notification.duration || 3000);
    }
    return () => {
      clearTimeout(timer);
      if (dismissTimer) clearTimeout(dismissTimer);
    };
    // eslint-disable-next-line
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => onClose(notification.id), 300);
  };

  let icon = null;
  let color = '';
  switch (notification.type) {
    case 'success':
      icon = <CheckCircle className="h-5 w-5 text-green-600" />;
      color = 'bg-green-50 border-green-200 text-green-900';
      break;
    case 'error':
      icon = <AlertCircle className="h-5 w-5 text-red-600" />;
      color = 'bg-red-50 border-red-200 text-red-900';
      break;
    case 'warning':
      icon = <AlertCircle className="h-5 w-5 text-yellow-600" />;
      color = 'bg-yellow-50 border-yellow-200 text-yellow-900';
      break;
    case 'info':
    default:
      icon = <Clock className="h-5 w-5 text-orange-600" />;
      color = 'bg-orange-50 border-orange-200 text-orange-900';
      break;
  }

  return (
    <div
      className={`flex items-start gap-3 rounded-md border shadow-lg px-4 py-3 mb-2 w-80 transition-all duration-300 ${color} ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'} ${isLeaving ? 'opacity-0 -translate-x-8' : ''}`}
      role="alert"
    >
      <div>{icon}</div>
      <div className="flex-1">
        <div className="font-semibold">{notification.title}</div>
        {notification.message && <div className="text-sm">{notification.message}</div>}
      </div>
      <button onClick={handleClose} className="ml-2 p-1 rounded hover:bg-gray-200 focus:outline-none">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

interface ToastContextType {
  notifications: ToastNotification[];
  addNotification: (type: ToastNotification['type'], title: string, message?: string, duration?: number, persistent?: boolean) => string;
  removeNotification: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  const addNotification = (
    type: ToastNotification['type'],
    title: string,
    message?: string,
    duration?: number,
    persistent?: boolean
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    setNotifications((prev) => [
      ...prev,
      { id, type, title, message, duration, persistent },
    ]);
    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  return (
    <ToastContext.Provider value={{ notifications, addNotification, removeNotification }}>
      {children}
      <ToastContainer notifications={notifications} removeNotification={removeNotification} />
    </ToastContext.Provider>
  );
};

export const useToastNotifications = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToastNotifications must be used within ToastProvider');
  return ctx;
};

export const ToastContainer: React.FC<{ notifications: ToastNotification[]; removeNotification: (id: string) => void }> = ({ notifications, removeNotification }) => (
  <div className="fixed top-4 right-4 z-50 flex flex-col items-end pointer-events-none">
    {notifications.map((n) => (
      <Toast key={n.id} notification={n} onClose={removeNotification} />
    ))}
  </div>
); 